import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FileText, Printer, Download, Mail, Search, Calendar,
  ChevronLeft, Loader2, ArrowUpDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import SendEmailModal from '../../components/admin/SendEmailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const CustomerStatements = ({ onNavigateToCustomer, initialAccountId }) => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(initialAccountId || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [invSort, setInvSort] = useState({ field: 'date', dir: 'asc' });
  const [autoGenerate, setAutoGenerate] = useState(!!initialAccountId);
  const printRef = useRef(null);

  // Default date range: last 90 days
  useEffect(() => {
    const now = new Date();
    const past = new Date(now);
    past.setDate(past.getDate() - 90);
    setToDate(now.toISOString().slice(0, 10));
    setFromDate(past.toISOString().slice(0, 10));
  }, []);

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/crm/accounts?limit=500`, { credentials: 'include' });
        const data = await res.json();
        const items = data.items || [];
        setCustomers(items);
        if (initialAccountId) {
          const match = items.find(c => (c.id || c.account_id) === initialAccountId);
          if (match) setSearchQuery(match.name || match.company_name || '');
        }
      } catch { /* ignore */ }
    };
    fetchCustomers();
  }, [initialAccountId]);

  const generateStatement = useCallback(async () => {
    if (!selectedCustomer) { toast.error('Select a customer'); return; }
    setLoading(true);
    setStatement(null);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const res = await fetch(`${API_URL}/api/statements/customer/${selectedCustomer}?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to generate statement');
      setStatement(await res.json());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer, fromDate, toDate]);

  // Auto-generate when navigated from customer detail
  useEffect(() => {
    if (autoGenerate && selectedCustomer && fromDate && toDate) {
      setAutoGenerate(false);
      generateStatement();
    }
  }, [autoGenerate, selectedCustomer, fromDate, toDate, generateStatement]);

  const handleSendStatementEmail = async (requestData) => {
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    const res = await fetch(`${API_URL}/api/statements/customer/${selectedCustomer}/email?${params}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Email failed');
    return data;
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      await html2pdf().set({
        margin: [10, 10, 10, 10],
        filename: `Statement_${statement.customer.name.replace(/\s+/g, '_')}_${statement.period.from}_${statement.period.to}.pdf`,
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }).from(printRef.current).save();
      toast.success('PDF downloaded');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  // Sort invoices
  const toggleInvSort = (field) => {
    setInvSort(prev => prev.field === field
      ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: 'asc' }
    );
  };

  const sortedInvoices = statement ? [...statement.invoices].sort((a, b) => {
    let cmp = 0;
    if (invSort.field === 'date') cmp = a.date.localeCompare(b.date);
    else if (invSort.field === 'due_date') cmp = (a.due_date || '').localeCompare(b.due_date || '');
    else if (invSort.field === 'total') cmp = a.total - b.total;
    else if (invSort.field === 'due') cmp = a.due - b.due;
    return invSort.dir === 'desc' ? -cmp : cmp;
  }) : [];

  const filteredCustomers = customers.filter(c => {
    const name = (c.name || c.company_name || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  return (
    <>
      {/* Print styles - monochrome-friendly */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          [data-testid="statement-print-area"], [data-testid="statement-print-area"] * { visibility: visible; }
          [data-testid="statement-print-area"] { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          [data-testid="no-print"] { display: none !important; }

          /* Force monochrome-safe aging summary */
          [data-testid="aging-summary"] > div {
            background: white !important;
            color: #000 !important;
            border: 2px solid #000 !important;
          }
          [data-testid="aging-summary"] > div p {
            color: #000 !important;
          }
          /* Progressive border thickness to indicate severity */
          [data-testid="aging-current"] { border-width: 1px !important; }
          [data-testid="aging-days_30"] { border-width: 2px !important; border-style: dashed !important; }
          [data-testid="aging-days_60"] { border-width: 2px !important; }
          [data-testid="aging-days_90"] { border-width: 3px !important; background: #f0f0f0 !important; }
          [data-testid="aging-days_120_plus"] { border-width: 3px !important; background: #d9d9d9 !important; }

          /* Remove all color from text */
          .text-green-600, .text-green-700, .text-yellow-700, .text-orange-700, .text-red-700, .text-red-800 {
            color: #000 !important;
          }
          .text-\\[\\#8B7355\\] { color: #000 !important; }
          .bg-\\[\\#8B7355\\]\\/5 { background: #f5f5f5 !important; }

          /* Ensure table readability */
          table { border-collapse: collapse; }
          th, td { border: 1px solid #ccc !important; }
          thead tr { background: #f0f0f0 !important; }

          /* Balance Due footer */
          .text-3xl.font-bold { color: #000 !important; }
        }
      `}</style>

      <div className="space-y-6" data-testid="customer-statements-page">
        {/* Back button */}
        {statement && (
          <button onClick={() => setStatement(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700" data-testid="back-to-generate">
            <ChevronLeft className="w-4 h-4" /> Back to Statement Generator
          </button>
        )}

        {!statement ? (
          <>
            {/* Generator */}
            <div className="bg-white rounded-lg border p-6" data-testid="statement-generator">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#8B7355]" />
                Generate Customer Statement
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-3">
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Customer</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" placeholder="Search customers..." value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                      data-testid="customer-search-input" />
                  </div>
                  {(searchQuery || !selectedCustomer) && (
                    <div className="mt-1 max-h-48 overflow-y-auto border rounded-lg bg-white shadow-sm">
                      {filteredCustomers.slice(0, 20).map(c => {
                        const id = c.id || c.account_id;
                        const name = c.name || c.company_name;
                        return (
                          <button key={id} onClick={() => { setSelectedCustomer(id); setSearchQuery(name); }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-[#f5f5f0] ${selectedCustomer === id ? 'bg-[#8B7355]/10 font-medium' : ''}`}
                            data-testid={`customer-option-${id}`}>
                            {name}
                          </button>
                        );
                      })}
                      {filteredCustomers.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">No customers found</p>}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">From</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                      data-testid="from-date-input" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">To</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#8B7355]/30 focus:border-[#8B7355]"
                      data-testid="to-date-input" />
                  </div>
                </div>
                <div className="flex items-end">
                  <Button onClick={generateStatement} disabled={!selectedCustomer || loading}
                    className="w-full bg-[#8B7355] hover:bg-[#6B5B4A] text-white" data-testid="generate-statement-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
                    Generate Statement
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick Periods */}
            <div className="bg-white rounded-lg border p-4">
              <p className="text-sm font-medium text-gray-600 mb-2">Quick Periods</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { label: 'Last 30 Days', days: 30 },
                  { label: 'Last 60 Days', days: 60 },
                  { label: 'Last 90 Days', days: 90 },
                  { label: 'This Month', preset: 'month' },
                  { label: 'Last Month', preset: 'last-month' },
                  { label: 'This Year', preset: 'year' },
                ].map(p => (
                  <button key={p.label} data-testid={`quick-period-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => {
                      const now = new Date();
                      let from, to;
                      if (p.days) { to = now; from = new Date(now); from.setDate(from.getDate() - p.days); }
                      else if (p.preset === 'month') { from = new Date(now.getFullYear(), now.getMonth(), 1); to = now; }
                      else if (p.preset === 'last-month') { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
                      else if (p.preset === 'year') { from = new Date(now.getFullYear(), 0, 1); to = now; }
                      setFromDate(from.toISOString().slice(0, 10));
                      setToDate(to.toISOString().slice(0, 10));
                    }}
                    className="px-3 py-1.5 text-xs rounded-full border border-[#8B7355]/30 text-[#8B7355] hover:bg-[#8B7355]/10 transition-colors">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Action Bar */}
            <div className="flex items-center justify-between" data-testid="no-print">
              <h2 className="text-lg font-semibold" data-testid="statement-title">
                Statement: {statement.customer.name}
              </h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-statement-btn">
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={handlePDF} disabled={exporting} data-testid="pdf-statement-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} PDF
                </Button>
                <Button size="sm" onClick={() => setShowSendEmailModal(true)}
                  className="bg-[#8B7355] hover:bg-[#6B5B4A] text-white" data-testid="email-statement-btn">
                  <Mail className="w-4 h-4 mr-1" />
                  Email to Customer
                </Button>
              </div>
            </div>

            {/* ═══ Printable Statement ═══ */}
            <div ref={printRef} data-testid="statement-print-area" className="bg-white rounded-lg border" style={{ fontFamily: 'Arial, sans-serif' }}>

              {/* Header: Company + STATEMENT */}
              <div className="flex justify-between items-start p-8 pb-4">
                <div>
                  {statement.company.logo_url && (
                    <img src={`${API_URL}${statement.company.logo_url}`} alt="Logo" className="h-14 mb-2" />
                  )}
                  <p className="text-sm text-gray-600 font-semibold">{statement.company.name}</p>
                  <p className="text-xs text-gray-500">
                    {statement.company.address_line1}{statement.company.city ? `, ${statement.company.city}` : ''}
                    {statement.company.state ? `, ${statement.company.state}` : ''} {statement.company.zip}
                  </p>
                  <p className="text-xs text-gray-500">{statement.company.phone} | {statement.company.email}</p>
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-[#8B7355] tracking-tight">STATEMENT</h1>
                  <p className="text-sm text-gray-600 mt-1">Date: {new Date().toLocaleDateString()}</p>
                  <p className="text-sm text-gray-600">Period: {statement.period.from} to {statement.period.to}</p>
                </div>
              </div>

              <div className="mx-8 border-t border-[#8B7355]" />

              {/* Bill To + Account Summary */}
              <div className="flex justify-between items-start p-8 py-5">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
                  <p className="font-bold text-base">{statement.customer.name}</p>
                  {statement.customer.address?.street && <p className="text-sm text-gray-600">{statement.customer.address.street}</p>}
                  {statement.customer.address?.city && (
                    <p className="text-sm text-gray-600">
                      {statement.customer.address.city}{statement.customer.address.state ? `, ${statement.customer.address.state}` : ''} {statement.customer.address.zip}
                    </p>
                  )}
                  {statement.customer.email && <p className="text-sm text-gray-500">{statement.customer.email}</p>}
                  <p className="text-xs text-gray-400 mt-1">Account: {statement.customer.id}</p>
                </div>
                <div className="border rounded-lg overflow-hidden" data-testid="account-summary">
                  <table className="text-sm">
                    <tbody>
                      <tr className="border-b">
                        <td className="px-4 py-2 text-gray-600 font-medium">Total Invoiced</td>
                        <td className="px-4 py-2 text-right font-semibold">{fmt(statement.summary.total_invoiced)}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-4 py-2 text-gray-600 font-medium">Total Payments</td>
                        <td className="px-4 py-2 text-right font-semibold text-green-600">-{fmt(statement.summary.total_payments)}</td>
                      </tr>
                      <tr className="bg-[#8B7355]/5">
                        <td className="px-4 py-2 font-bold text-[#8B7355]">Balance Due</td>
                        <td className="px-4 py-2 text-right font-bold text-[#8B7355] text-lg">{fmt(statement.summary.closing_balance)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ─── Invoices Table ─── */}
              <div className="px-8 pb-2">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Invoices</h3>
                <table className="w-full text-sm border-collapse" data-testid="invoices-table">
                  <thead>
                    <tr className="bg-[#f5f5f0] border-y">
                      {[
                        { key: 'invoice_number', label: 'Invoice #', align: 'left' },
                        { key: 'order_id', label: 'Order #', align: 'left' },
                        { key: 'date', label: 'Date', align: 'left', sortable: true },
                        { key: 'due_date', label: 'Due Date', align: 'left', sortable: true },
                        { key: 'total', label: 'Total', align: 'right', sortable: true },
                        { key: 'paid', label: 'Paid', align: 'right' },
                        { key: 'due', label: 'Amount Due', align: 'right', sortable: true },
                      ].map(col => (
                        <th key={col.key}
                          className={`px-3 py-2 font-semibold text-gray-700 text-${col.align} ${col.sortable ? 'cursor-pointer select-none' : ''}`}
                          onClick={col.sortable ? () => toggleInvSort(col.key) : undefined}>
                          <span className="flex items-center gap-1" style={{ justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                            {col.label}
                            {col.sortable && <ArrowUpDown className="w-3 h-3 text-gray-400" />}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedInvoices.map((inv, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50/60">
                        <td className="px-3 py-2 font-mono text-xs font-medium">{inv.invoice_number}</td>
                        <td className="px-3 py-2 font-mono text-xs">{inv.order_id}</td>
                        <td className="px-3 py-2">{inv.date}</td>
                        <td className="px-3 py-2">{inv.due_date}</td>
                        <td className="px-3 py-2 text-right">{fmt(inv.total)}</td>
                        <td className="px-3 py-2 text-right text-green-600">{inv.paid > 0 ? fmt(inv.paid) : '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmt(inv.due)}</td>
                      </tr>
                    ))}
                    {sortedInvoices.length === 0 && (
                      <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No invoices in this period</td></tr>
                    )}
                    {/* Totals */}
                    {sortedInvoices.length > 0 && (
                      <tr className="bg-[#f5f5f0] border-t-2 border-gray-300 font-bold">
                        <td colSpan={4} className="px-3 py-2 text-right">Totals</td>
                        <td className="px-3 py-2 text-right">{fmt(statement.summary.total_invoiced)}</td>
                        <td className="px-3 py-2 text-right text-green-600">{fmt(statement.summary.total_paid)}</td>
                        <td className="px-3 py-2 text-right text-[#8B7355]">{fmt(statement.summary.total_due)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ─── Payments Table (hidden if no payments) ─── */}
              {statement.payments.length > 0 && (
                <div className="px-8 pt-4 pb-2">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Payments</h3>
                  <table className="w-full text-sm border-collapse" data-testid="payments-table">
                    <thead>
                      <tr className="bg-[#f5f5f0] border-y">
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Payment #</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Method</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Reference</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.payments.map((pay, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50/60">
                          <td className="px-3 py-2 font-mono text-xs font-medium">{pay.payment_number}</td>
                          <td className="px-3 py-2">{pay.date}</td>
                          <td className="px-3 py-2">{pay.method}</td>
                          <td className="px-3 py-2 text-gray-500">{pay.reference || '—'}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-600">{fmt(pay.amount)}</td>
                        </tr>
                      ))}
                      <tr className="bg-[#f5f5f0] border-t-2 border-gray-300 font-bold">
                        <td colSpan={4} className="px-3 py-2 text-right">Total Payments</td>
                        <td className="px-3 py-2 text-right text-green-600">{fmt(statement.summary.total_payments)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* ─── Aging Summary ─── */}
              <div className="px-8 pt-6 pb-2">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Aging Summary</h3>
                <div className="grid grid-cols-5 gap-2" data-testid="aging-summary">
                  {[
                    { label: 'Current', key: 'current', color: 'bg-green-50 text-green-700 border-green-200' },
                    { label: '1-30 Days', key: 'days_30', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
                    { label: '31-60 Days', key: 'days_60', color: 'bg-orange-50 text-orange-700 border-orange-200' },
                    { label: '61-90 Days', key: 'days_90', color: 'bg-red-50 text-red-700 border-red-200' },
                    { label: 'Over 90', key: 'days_120_plus', color: 'bg-red-100 text-red-800 border-red-300' },
                  ].map(b => (
                    <div key={b.key} className={`border rounded-lg p-3 text-center ${b.color}`} data-testid={`aging-${b.key}`}>
                      <p className="text-xs font-medium">{b.label}</p>
                      <p className="text-lg font-bold mt-1">{fmt(statement.aging[b.key])}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ─── Footer ─── */}
              <div className="mx-8 mt-6 border-t border-[#8B7355]" />
              <div className="flex justify-between items-center px-8 py-6">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Thank you for your business. Please remit payment within 30 days.
                  <br />Questions? Contact {statement.company.email}
                </p>
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">Balance Due</p>
                  <p className="text-3xl font-bold text-[#8B7355]" data-testid="closing-balance">{fmt(statement.summary.closing_balance)}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Send Statement Email Modal */}
      {statement && (
        <SendEmailModal
          open={showSendEmailModal}
          onOpenChange={setShowSendEmailModal}
          emailType="statement"
          customer={statement.customer}
          statementData={{
            customerName: statement.customer.name,
            periodFrom: statement.period.from,
            periodTo: statement.period.to,
          }}
          onSend={handleSendStatementEmail}
        />
      )}
    </>
  );
};

export default CustomerStatements;
